import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

interface Colaborador {
  id: string;
  nome: string;
  cargo: string;
  avatar?: string;
  metricas: {
    totalTarefas: number;
    tarefasConcluidas: number;
    tarefasPendentes: number;
    tarefasAtrasadas: number;
    taxaConclusao: number;
  };
}

interface MetricasDepartamentoProps {
  departamento: {
    id: string;
    nome: string;
    descricao?: string;
  };
  metricas: {
    totalTarefas: number;
    tarefasConcluidas: number;
    tarefasConcluidasNoPrazo: number;
    tarefasConcluidasComAtraso: number;
    tarefasPendentes: number;
    tarefasAtrasadas: number;
    taxaConclusao: number;
    tempoMedioConclusao: string;
  };
  colaboradores?: Colaborador[];
  onExportar?: () => void;
}

const MetricasDepartamento: React.FC<MetricasDepartamentoProps> = ({
  departamento,
  metricas,
  colaboradores,
  onExportar
}) => {
  const obterCorTaxa = (taxa: number) => {
    if (taxa >= 80) return 'success';
    if (taxa >= 60) return 'warning';
    return 'error';
  };

  return (
    <Card sx={{ mb: 3, boxShadow: 3 }}>
      <CardContent>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" gutterBottom>
                {departamento.nome}
              </Typography>
              {departamento.descricao && (
                <Typography variant="body2" color="text.secondary">
                  {departamento.descricao}
                </Typography>
              )}
            </Box>
          </Box>
          {onExportar && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onExportar}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              Exportar
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Métricas Principais */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          {/* Total de Tarefas */}
          <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'action.hover', borderRadius: 2, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary">
              {metricas.totalTarefas}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total de Tarefas
            </Typography>
          </Box>

          {/* Tarefas Concluídas */}
          <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12), borderRadius: 2, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main">
              {metricas.tarefasConcluidas}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Concluídas
            </Typography>
          </Box>

          {/* Tarefas Pendentes */}
          <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12), borderRadius: 2, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main">
              {metricas.tarefasPendentes}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pendentes
            </Typography>
          </Box>

          {/* Tarefas Atrasadas */}
          <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12), borderRadius: 2, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
            <TrendingUpIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" color="error.main">
              {metricas.tarefasAtrasadas}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Atrasadas
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Detalhamento de Conclusão */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detalhamento de Conclusão
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Concluídas no Prazo:</Typography>
              <Chip
                label={metricas.tarefasConcluidasNoPrazo}
                color="success"
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Concluídas com Atraso:</Typography>
              <Chip
                label={metricas.tarefasConcluidasComAtraso}
                color="error"
                size="small"
              />
            </Box>
          </Stack>
        </Box>

        {/* Taxa de Conclusão */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              Taxa de Conclusão
            </Typography>
            <Typography variant="body1" fontWeight="bold" color={`${obterCorTaxa(metricas.taxaConclusao)}.main`}>
              {metricas.taxaConclusao}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={metricas.taxaConclusao}
            color={obterCorTaxa(metricas.taxaConclusao)}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        {/* Tempo Médio de Conclusão */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'action.hover', borderRadius: 2, transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
          <Typography variant="body1" fontWeight="bold">
            Tempo Médio de Conclusão
          </Typography>
          <Chip
            label={`${metricas.tempoMedioConclusao} dias`}
            color="primary"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        {/* Colaboradores do Departamento */}
        {colaboradores && colaboradores.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Colaboradores do Departamento ({colaboradores.length})
            </Typography>
            
            <Stack spacing={2} sx={{ mt: 2 }}>
              {colaboradores.map((colaborador) => (
                <Card key={colaborador.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {colaborador.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {colaborador.cargo}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${colaborador.metricas.taxaConclusao}%`}
                      color={obterCorTaxa(colaborador.metricas.taxaConclusao)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="h6" color="primary">
                        {colaborador.metricas.totalTarefas}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1, backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12), borderRadius: 1 }}>
                      <Typography variant="h6" color="success.main">
                        {colaborador.metricas.tarefasConcluidas}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Concluídas
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1, backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12), borderRadius: 1 }}>
                      <Typography variant="h6" color="warning.main">
                        {colaborador.metricas.tarefasPendentes}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pendentes
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 1, backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12), borderRadius: 1 }}>
                      <Typography variant="h6" color="error.main">
                        {colaborador.metricas.tarefasAtrasadas}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Atrasadas
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricasDepartamento;
